import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Float, useTexture } from "@react-three/drei";
import * as THREE from "three";
import { useScroll } from "../../scroll/ScrollProvider";

const LOGO_SRC = "/brand/five-quantum-gold.png";

/**
 * The Hero emblem: the FIVE QUANTUM crest rendered on a plane, suspended in
 * a void with a slow rotating halo of gold rings behind it. The whole group
 * floats and parallaxes off the camera.
 */
export function HeroEmblem() {
  const group = useRef<THREE.Group>(null);
  const halo = useRef<THREE.Group>(null);
  const { progress } = useScroll();
  const tex = useTexture(LOGO_SRC);
  // Premium texture filtering
  tex.anisotropy = 8;
  tex.colorSpace = THREE.SRGBColorSpace;

  // Plane sized to the texture's aspect (392 x 361)
  const aspect = 392 / 361;
  const height = 2.4;
  const width = height * aspect;

  useFrame((_state, delta) => {
    if (halo.current) halo.current.rotation.z -= delta * 0.08;
    if (group.current) {
      // Fade the emblem out as the user enters section 1
      const fade = Math.max(0, 1 - progress * 4);
      group.current.scale.setScalar(0.85 + fade * 0.18);
      (group.current as any).visible = fade > 0.001;
    }
  });

  return (
    <Float floatIntensity={0.35} rotationIntensity={0.12} speed={0.7}>
      <group ref={group}>
        {/* Halo: concentric rings spinning behind the crest */}
        <group ref={halo} position={[0, 0, -0.1]}>
          {[0, 1, 2, 3].map((i) => {
            const radius = 1.55 + i * 0.18;
            const tube = i === 0 ? 0.018 : 0.006;
            return (
              <mesh key={i} rotation={[Math.PI / 2, 0, 0]}>
                <torusGeometry args={[radius, tube, 24, 160]} />
                <meshStandardMaterial
                  color="#e7c98a"
                  metalness={1}
                  roughness={0.22}
                  emissive="#3a2a0a"
                  emissiveIntensity={0.6}
                  toneMapped={false}
                />
              </mesh>
            );
          })}
        </group>

        {/* Soft gold glow disc behind the crest */}
        <mesh position={[0, 0, -0.2]}>
          <circleGeometry args={[1.6, 64]} />
          <meshBasicMaterial color="#3a2a0a" transparent opacity={0.55} toneMapped={false} />
        </mesh>

        {/* The crest itself */}
        <mesh>
          <planeGeometry args={[width, height]} />
          <meshBasicMaterial
            map={tex}
            transparent
            toneMapped={false}
            depthWrite={false}
          />
        </mesh>

        {/* Inner glow */}
        <pointLight position={[0, 0, 0.6]} intensity={1.2} color="#f6d28a" distance={3.2} decay={2} />
      </group>
    </Float>
  );
}

useTexture.preload(LOGO_SRC);
